/**
 * Arena Home Page - The Intelligent Standard
 *
 * Elegant flowing curves, sophisticated typography,
 * clear visual hierarchy between sections.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Footer } from '@/components/Footer';
import { LoginModal } from '@/components/LoginModal';
import { Logo, LogoAnimated } from '@/components/Logo';
import { ProtectedLink } from '@/components/ProtectedLink';
import { ProviderLogo } from '@/components/shared/ProviderLogo';
import { ApiError, apiRequest } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMeridianUser } from '@/contexts/FeatureFlagContext';
import { logDebug, logError, logInfo } from '@/lib/logger';

// Animated counter hook
function useCountUp(
  end: number,
  duration: number = 1500,
  startDelay: number = 0
) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const delayTimer = setTimeout(() => setStarted(true), startDelay);
    return () => clearTimeout(delayTimer);
  }, [startDelay]);

  useEffect(() => {
    if (!started || end === 0) return;

    const startTime = Date.now();
    const startValue = 0;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function - ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(startValue + (end - startValue) * eased);

      setCount(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [end, duration, started]);

  return count;
}

// Cell dots animation used in stat displays
const CellDots = ({ baseOpacity = 0.85 }: { baseOpacity?: number }) => (
  <div className="flex justify-center gap-1 mb-2 h-4">
    {[0, 1, 2].map(col => (
      <div
        key={col}
        className="cell-pop"
        style={{
          width: '8px',
          height: '12px',
          borderRadius: '2px',
          background: `rgba(255,255,255,${baseOpacity - col * 0.15})`,
          opacity: 0,
          animationDelay: `${col * 50}ms`,
        }}
      />
    ))}
  </div>
);

// Unified stat display - handles both numeric (animated) and text values
const StatDisplay = ({
  value,
  label,
  delay = 0,
  animate = false,
}: {
  value: number | string;
  label: string;
  delay?: number;
  animate?: boolean;
}) => {
  const numericValue = typeof value === 'number' ? value : 0;
  const count = useCountUp(animate ? numericValue : 0, 1200, delay + 200);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const displayValue = animate ? count.toLocaleString() : value;

  return (
    <div className="text-center">
      {visible && <CellDots baseOpacity={animate ? 0.85 : 0.65} />}
      <div
        className={`font-serif text-2xl font-medium ${animate ? 'tabular-nums' : ''} ${visible ? 'number-reveal' : 'opacity-0'}`}
        style={{ color: '#FFFFFF', animationDelay: '80ms' }}
      >
        {displayValue}
      </div>
      <div
        className={`text-[11px] uppercase tracking-widest mt-1 ${visible ? 'number-reveal' : 'opacity-0'}`}
        style={{ color: 'rgba(255,255,255,0.45)', animationDelay: '150ms' }}
      >
        {label}
      </div>
    </div>
  );
};

interface LeaderboardEntry {
  model_key: string;
  display_name: string;
  rating: number;
  matches: number;
}

interface RecentVote {
  runId: string;
  promptText: string;
  category: string | null;
  latestVote: string;
  totalVotes: number;
  uniqueVoters: number;
  winner: {
    name: string;
    modelKey: string;
    votes: number;
  } | null;
  runnerUp: {
    name: string;
    modelKey: string;
    votes: number;
  } | null;
}

interface SeedPrompt {
  id: string;
  promptText: string;
  category: string | null;
  createdAt: string;
  validResultsCount: number;
  totalVotes: number;
  currentUserHasVoted: boolean;
}

// ============================================================================
// Shared Components & Utilities
// ============================================================================

/** Format model display name - removes dates, extracts version info */
function formatModelName(
  name: string,
  format: 'short' | 'full' = 'full'
): string {
  const cleaned = name.replace(' Baseline', '').replace(/\s*\([^)]*\)/, '');
  const parts = cleaned.split(' ');

  if (format === 'short') {
    // For leader display: "Opus 4.5" from "Claude Opus 4.5"
    if (parts.length >= 3 && /^\d/.test(parts[2])) {
      return `${parts[1]} ${parts[2]}`;
    }
    return parts.slice(0, 2).join(' ');
  }

  // Full format: "Opus 4.5" or full name if no version
  if (parts.length >= 3 && /^\d/.test(parts[2])) {
    return `${parts[1]} ${parts[2]}`;
  }
  return cleaned;
}

/** Chevron icon pointing right */
const ChevronRight = ({
  size = 16,
  color = 'currentColor',
  className = '',
}: {
  size?: number;
  color?: string;
  className?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

/** Section header with title, subtitle, and optional link */
const SectionHeader = ({
  title,
  subtitle,
  linkTo,
  linkText = 'View all',
  variant = 'light',
}: {
  title: string;
  subtitle: string;
  linkTo?: string;
  linkText?: string;
  variant?: 'light' | 'dark';
}) => {
  const isDark = variant === 'dark';
  const LinkComponent = linkTo?.startsWith('/leaderboard')
    ? ProtectedLink
    : Link;

  return (
    <div className="flex items-end justify-between mb-10">
      <div>
        <h2
          className="font-serif text-2xl font-normal mb-2"
          style={{ color: isDark ? '#FFFFFF' : '#1A1A1A' }}
        >
          {title}
        </h2>
        <p
          className="text-sm"
          style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#6B6560' }}
        >
          {subtitle}
        </p>
      </div>
      {linkTo && (
        <LinkComponent
          to={linkTo}
          className="text-sm font-medium flex items-center gap-1 transition-opacity hover:opacity-70"
          style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#8B2332' }}
        >
          {linkText}
          <ChevronRight size={16} />
        </LinkComponent>
      )}
    </div>
  );
};

/** Dot separator for metadata items */
const DotSeparator = () => <span style={{ color: '#D1C9C4' }}>·</span>;

/** Curved SVG divider between sections */
const CurvedDivider = ({
  fromColor,
  toColor,
  flip = false,
}: {
  fromColor: string;
  toColor: string;
  flip?: boolean;
}) => (
  <svg
    className="block w-full"
    viewBox="0 0 1440 80"
    preserveAspectRatio="none"
    style={{ background: fromColor, marginBottom: '-1px' }}
  >
    <path
      d={
        flip
          ? 'M0,30 Q480,70 720,30 Q960,-10 1440,30 L1440,80 L0,80 Z'
          : 'M0,30 Q360,70 720,30 Q1080,-10 1440,30 L1440,80 L0,80 Z'
      }
      fill={toColor}
    />
  </svg>
);

/** Metadata line with category, date, and optional vote count */
const PromptMetadata = ({
  category,
  createdAt,
  totalVotes,
}: {
  category: string | null;
  createdAt: string;
  totalVotes?: number;
}) => {
  const formattedDate = new Date(createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="flex items-center gap-2 mt-1.5">
      {category && (
        <>
          <span
            className="text-xs truncate"
            style={{ color: '#8B8580', maxWidth: '120px' }}
          >
            {category}
          </span>
          <DotSeparator />
        </>
      )}
      <span className="text-xs tabular-nums" style={{ color: '#A9A49E' }}>
        {formattedDate}
      </span>
      {totalVotes !== undefined && totalVotes > 0 && (
        <>
          <DotSeparator />
          <span className="text-xs tabular-nums" style={{ color: '#4A7C4A' }}>
            {totalVotes} vote{totalVotes !== 1 ? 's' : ''}
          </span>
        </>
      )}
    </div>
  );
};

/** Card for seed prompts - compact list-style design */
const SeedPromptCard = ({
  prompt,
  index,
}: {
  prompt: SeedPrompt;
  index: number;
}) => {
  const hasVoted = prompt.currentUserHasVoted;

  return (
    <ProtectedLink
      to={`/arena/${prompt.id}?source=seed`}
      className="group block battle-card"
      style={{ animationDelay: `${index * 40}ms` }}
    >
      <div
        className="px-4 py-3.5 rounded-lg transition-all duration-200 group-hover:bg-white group-hover:shadow-md"
        style={{
          background: hasVoted
            ? 'rgba(200,195,190,0.4)'
            : 'rgba(255,255,255,0.6)',
          border: '1px solid transparent',
          opacity: hasVoted ? 0.5 : 1,
        }}
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p
              className="text-sm leading-snug line-clamp-2 transition-colors duration-200 group-hover:text-[#1A1A1A]"
              style={{ color: hasVoted ? '#6B6560' : '#3A3530' }}
            >
              {prompt.promptText}
            </p>
            <PromptMetadata
              category={prompt.category}
              createdAt={prompt.createdAt}
              totalVotes={prompt.totalVotes}
            />
          </div>
          <ChevronRight
            size={16}
            color={hasVoted ? '#A9A49E' : '#8B2332'}
            className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-all duration-200 mt-1"
          />
        </div>
      </div>
    </ProtectedLink>
  );
};

/** Card for recent battle votes */
const RecentBattleCard = ({
  vote,
  index,
}: {
  vote: RecentVote;
  index: number;
}) => (
  <ProtectedLink
    to={`/arena/${vote.runId}`}
    className="group block battle-card"
    style={{ animationDelay: `${index * 50}ms` }}
  >
    <div
      className="p-5 rounded-xl transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-xl group-hover:border-[#444]"
      style={{ background: '#242424', border: '1px solid #333' }}
    >
      {vote.winner && (
        <div className="flex items-start gap-3">
          <div className="transition-transform duration-300 group-hover:scale-110 mt-3">
            <ProviderLogo
              modelKey={vote.winner.modelKey}
              size={32}
              showBackground={false}
            />
          </div>
          <div className="flex-1 min-w-0">
            <span
              className="text-[10px] uppercase tracking-wider mb-0.5 block"
              style={{ color: 'rgba(16, 185, 129, 0.8)' }}
            >
              Winner
            </span>
            <span
              className="font-medium text-sm truncate block transition-colors duration-300 group-hover:text-white"
              style={{ color: 'rgba(255,255,255,0.9)' }}
            >
              {vote.winner.name.replace(' Baseline', '')}
            </span>
          </div>
          <ChevronRight
            size={16}
            color="rgba(255,255,255,0.5)"
            className="opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1 flex-shrink-0"
          />
        </div>
      )}
      <p
        className="text-sm leading-relaxed line-clamp-2 transition-colors duration-300 group-hover:text-white/70"
        style={{ color: 'rgba(255,255,255,0.6)' }}
      >
        {vote.promptText}
      </p>
    </div>
  </ProtectedLink>
);

// Feature flag for autogenerated prompt buttons
const AUTOGENERATED_PROMPT_BUTTONS = false;

// Maximum prompt length (characters)
const MAX_PROMPT_LENGTH = 30000;

// Alert icon for error states
const AlertCircleIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#8B2332"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// Error message display
const PromptError = ({ message }: { message: string }) => (
  <div className="flex items-center gap-1.5">
    <AlertCircleIcon />
    <span className="text-sm font-medium" style={{ color: '#8B2332' }}>
      {message}
    </span>
  </div>
);

// Prompt length indicator with color states
const PromptLengthIndicator = ({
  length,
  maxLength,
  isApproachingLimit,
  isOverLimit,
}: {
  length: number;
  maxLength: number;
  isApproachingLimit: boolean;
  isOverLimit: boolean;
}) => {
  const getColor = () => {
    if (isOverLimit) return '#8B2332';
    if (isApproachingLimit) return '#B45309';
    return '#9A958E';
  };

  const getText = () => {
    if (isOverLimit) {
      return `Prompt too long (${length.toLocaleString()} / ${maxLength.toLocaleString()})`;
    }
    if (isApproachingLimit) {
      return `${length.toLocaleString()} / ${maxLength.toLocaleString()} characters`;
    }
    if (length > 0) {
      return `${length.toLocaleString()} characters`;
    }
    return 'What will you build?';
  };

  return (
    <span
      className="text-sm"
      style={{
        color: getColor(),
        fontWeight: isOverLimit ? 500 : 'normal',
      }}
    >
      {(isApproachingLimit || length > 0) && !isOverLimit ? (
        <span className="tabular-nums">{getText()}</span>
      ) : (
        getText()
      )}
    </span>
  );
};

const CATEGORIES = [
  { id: 'Professional Finance', label: 'Finance' },
  { id: 'Corporate Finance & FP&A', label: 'FP&A' },
  { id: 'SMB & Personal', label: 'SMB' },
  { id: 'Operations & Supply Chain', label: 'Operations' },
  { id: 'Academic & Research', label: 'Academic' },
  { id: 'Creative & Generative', label: 'Creative' },
];

const SAMPLE_PROMPTS: Record<string, string[]> = {
  'Professional Finance': [
    'Build a full three-statement model for a sample company starting with $120M revenue in Year 0, growing 8% annually, 55% gross margin, and 10% operating margin. Include linked IS/BS/CF statements, working-capital schedules, and auto-balancing cash flow.',
    'Create a DCF valuation using sample inputs: revenue growing from $50M to $90M over five years, EBITDA margin expanding from 18% to 24%, WACC 9%, and terminal growth 2.5%. Produce enterprise value, equity value, and a WACC vs. terminal growth sensitivity table.',
    'Generate a simplified LBO model using an $80M purchase price, 60% debt financing at 7% interest, 5-year term, and a 6x exit EBITDA multiple. Include sources/uses, debt amortization, interest schedule, and investor IRR at exit.',
    'Produce a comps analysis using five sample companies with revenues, EBITDA, and market caps. Calculate EV/Revenue and EV/EBITDA multiples, then use median and 25th/75th percentile ranges to derive an implied valuation for a target with $30M revenue and $6M EBITDA.',
  ],

  'Corporate Finance & FP&A': [
    'Create a rolling 12-month forecast using sample monthly actuals for revenue, COGS, and operating expenses for the past 9 months. Use straight-line growth assumptions (2% MoM for revenue, 1% for opex) to project the next 12 months.',
    'Build a product-level revenue forecast for three products with sample base volumes and prices. Include seasonality weights (e.g., Q4 peak), and output monthly revenue for the next year with base, downside (-10%), and upside (+10%) scenarios.',
    'Generate a budget vs. actuals report using provided data for 6 months of revenue and expenses. Calculate absolute and percentage variance, flag variances >10%, and summarize totals in a dashboard.',
    'Construct a 13-week cash flow forecast starting with a sample $500k cash balance. Include AR collections (30-day lag), AP payments (45-day lag), payroll ($60k weekly), and output weekly ending cash and minimum liquidity.',
  ],

  'SMB & Personal': [
    'Create a monthly personal budget tracker using sample income ($5,000) and expenses across 10 categories with real starting values. Show actual vs. budget, remaining balance, and a year-to-date savings total.',
    'Generate a small-business P&L sheet for a sample café, including monthly revenue from three product lines, COGS percentages, labor costs, rent, and utilities. Output net income by month and year-end totals.',
    'Build a subscription tracker preloaded with 12 sample subscriptions, including costs, billing frequency, renewal dates, and category tags. Calculate the monthly and annual total spend automatically.',
    'Make a travel expense planner with sample planned costs for flights, hotel, food, and activities, plus actual costs for 3 days. Show variances and total per-person cost for a three-person trip.',
  ],

  'Operations & Supply Chain': [
    'Create an inventory tracker pre-filled with 20 sample SKUs, including on-hand quantity, reorder points, lead times, and supplier names. Flag items below reorder point and calculate recommended reorder quantity.',
    'Build a demand forecast model using 24 months of sample historical sales for one product. Compare a 3-month SMA, 6-month SMA, and exponential smoothing forecast, then project the next 6 months.',
    'Generate a production schedule with sample jobs (10 orders) including required hours, due dates, and machine availability. Allocate jobs by earliest due date and output machine utilization percentages.',
    'Create a logistics cost model using sample shipments with weight, distance, and mode (air/ground). Calculate cost-per-shipment using sample cost formulas and summarize total weekly cost by mode.',
  ],

  'Academic & Research': [
    'Create a regression workbook using a sample dataset of 50 observations with variables (Y, X1, X2). Run an OLS regression, output coefficients, t-stats, p-values, R^2, and create a predicted vs. actual scatterplot.',
    'Build a time-series analysis using 36 months of sample GDP index data. Fit an ARIMA(1,1,1) and exponential smoothing model, then forecast the next 6 periods with confidence intervals.',
    'Generate a Monte Carlo simulation with a sample project requiring an initial $1,000 investment and uncertain monthly returns modeled with a mean of 1% and SD 3%. Run 5,000 iterations and output expected return and probability of loss.',
    'Construct a cost-benefit analysis for a sample public policy with benefits and costs over 10 years. Include discounting at 3%, NPV of benefits vs. costs, and a sensitivity table for discount rates from 1-7%.',
  ],

  'Creative & Generative': [
    'Generate a 30x30 pixel-art grid and prefill 15 cells with sample hex colors to demonstrate output. Apply conditional formatting to display the colors directly in the grid.',
    'Create a functional Sudoku puzzle with a fully filled 9x9 solved grid plus a playable version with 30 given numbers. Include formulas to check rule violations.',
    'Build a fractal generator using iterative calculations on a 50x50 grid. Preload sample coordinates and show iteration counts, then apply conditional formatting to color-code iteration depth.',
    'Produce a simple spreadsheet game where a player moves a token across a 10x10 grid. Prefill starting positions and include formulas that update the position based on user-entered commands.',
  ],
};

function getSamplePrompt(categoryId: string): string | null {
  const prompts = SAMPLE_PROMPTS[categoryId];
  if (!prompts || prompts.length === 0) {
    return null;
  }
  const randomIndex = Math.floor(Math.random() * prompts.length);
  return prompts[randomIndex];
}

// How It Works section with scroll-triggered animation
const HowItWorksSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Only trigger once
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      num: 1,
      title: 'Generate',
      desc: 'Write a prompt for the spreadsheet you need',
    },
    {
      num: 2,
      title: 'Compare',
      desc: 'Review blind outputs from competing models',
    },
    {
      num: 3,
      title: 'Vote',
      desc: 'Pick the winner and shape the leaderboard',
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="pt-12 pb-20 px-6"
      style={{ background: '#F5F0EB' }}
    >
      <style>{`
        .cell-animate {
          transform: scale(0);
          opacity: 0;
        }
        .cell-animate.visible {
          animation: cellFillExpand 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>
      <div className="max-w-3xl mx-auto text-center">
        <h2
          className="font-serif text-3xl font-normal mb-4"
          style={{ color: '#1A1A1A' }}
        >
          How It Works
        </h2>
        <p className="text-base mb-14" style={{ color: '#6B6560' }}>
          Help build the benchmark for AI spreadsheet generation
        </p>

        <div className="grid grid-cols-3 gap-10">
          {steps.map((step, stepIdx) => (
            <div key={step.num}>
              {/* Cell grid indicator - fills based on step number */}
              <div className="flex justify-center gap-1 mb-4">
                {[0, 1, 2].map(col => (
                  <div key={col} className="flex flex-col gap-1">
                    {[0, 1, 2].map(row => {
                      const cellIndex = col * 3 + row;
                      const isFilled = cellIndex < step.num * 3;
                      // Stagger delay: step delay + column delay + row delay
                      const delay = stepIdx * 200 + col * 80 + row * 40;

                      return (
                        <div
                          key={row}
                          className={`w-3 h-3 rounded-sm cell-animate ${isVisible ? 'visible' : ''}`}
                          style={{
                            background: isFilled ? '#8B2332' : '#E8E0D8',
                            opacity: isFilled ? 1 - row * 0.15 : 0.4,
                            animationDelay: isVisible ? `${delay}ms` : '0ms',
                          }}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
              <h3
                className={`font-serif text-xl font-normal mb-2 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{
                  color: '#1A1A1A',
                  transitionDelay: `${stepIdx * 200 + 300}ms`,
                }}
              >
                {step.title}
              </h3>
              <p
                className={`text-sm leading-relaxed transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{
                  color: '#6B6560',
                  transitionDelay: `${stepIdx * 200 + 400}ms`,
                }}
              >
                {step.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/** Compact header with accent bar, title, badge, and link */
const CompactSectionHeader = ({
  title,
  badge,
  linkTo,
  linkText = 'View all',
}: {
  title: string;
  badge?: string;
  linkTo: string;
  linkText?: string;
}) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-3">
      <div className="w-1 h-5 rounded-full" style={{ background: '#8B2332' }} />
      <h3
        className="text-sm font-medium uppercase tracking-wide"
        style={{ color: '#6B6560' }}
      >
        {title}
      </h3>
      {badge && (
        <span
          className="text-xs px-2 py-0.5 rounded-full"
          style={{ background: '#E8E0D8', color: '#6B6560' }}
        >
          {badge}
        </span>
      )}
    </div>
    <Link
      to={linkTo}
      className="text-xs font-medium flex items-center gap-1 transition-opacity hover:opacity-70"
      style={{ color: '#8B2332' }}
    >
      {linkText}
      <ChevronRight size={14} />
    </Link>
  </div>
);

/** Seed prompts section for Meridian users */
const SeedPromptsSection = ({ prompts }: { prompts: SeedPrompt[] }) => (
  <section className="pb-16 px-6" style={{ background: '#F5F0EB' }}>
    <div className="max-w-4xl mx-auto">
      <CompactSectionHeader
        title="Seed Review Queue"
        badge="(MERIDIAN ONLY)"
        linkTo="/eval/unified"
      />
      <div
        className="rounded-xl p-2"
        style={{ background: 'rgba(232, 224, 216, 0.5)' }}
      >
        <div className="grid md:grid-cols-2 gap-2">
          {prompts.map((prompt, idx) => (
            <SeedPromptCard key={prompt.id} prompt={prompt} index={idx} />
          ))}
        </div>
      </div>
    </div>
  </section>
);

const BAR_COLORS = [
  '#8B2332',
  '#9E3A47',
  '#B15A5A',
  '#A67C6D',
  '#8E9080',
  '#7A8B8E',
  '#9A9590',
  '#A8A49E',
  '#B5B0A8',
  '#C2BDB5',
];

export default function ArenaHomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMeridianUser = useIsMeridianUser();
  const [prompt, setPrompt] = useState('');
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [stats, setStats] = useState<{
    totalBattles: number;
    totalModels: number;
    totalPrompts: number;
  }>({ totalBattles: 0, totalModels: 0, totalPrompts: 0 });
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(true);
  const [recentVotes, setRecentVotes] = useState<RecentVote[]>([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [seedPrompts, setSeedPrompts] = useState<SeedPrompt[]>([]);
  const [loadingSeed, setLoadingSeed] = useState(true);
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);

  // Derived state for prompt length validation
  const isPromptTooLong = prompt.length > MAX_PROMPT_LENGTH;
  const isApproachingLimit = prompt.length > MAX_PROMPT_LENGTH * 0.9;

  useEffect(() => {
    loadLeaderboard();
    loadStats();
    loadRecentVotes();
    loadSeedPrompts();
  }, []);

  // Log client connection on page load
  useEffect(() => {
    logInfo(
      `Client connected${user?.email ? `: ${user.email}` : ' (anonymous)'}`
    );
  }, []);

  async function loadLeaderboard() {
    try {
      const data = await apiRequest(
        '/api/leaderboard?source=unified&ratingType=btd'
      );
      const entries = data.entries || data.leaderboard || [];
      setLeaderboard(entries);
    } catch {
      setLeaderboard([]);
    } finally {
      setLoadingLeaderboard(false);
    }
  }

  async function loadStats() {
    const data = await apiRequest('/api/leaderboard/stats?source=unified');
    setStats({
      totalBattles: data.totalBattles,
      totalModels: data.totalModels,
      totalPrompts: data.totalPrompts,
    });
  }

  async function loadRecentVotes() {
    try {
      const data = await apiRequest('/api/leaderboard/recent-votes?limit=6');
      setRecentVotes(data.votes || []);
    } catch (err) {
      logError('Failed to load recent votes', err);
      setRecentVotes([]);
    } finally {
      setLoadingRecent(false);
    }
  }

  async function loadSeedPrompts() {
    try {
      // Fetch only featured prompts, sorted by fewest votes first
      const data = await apiRequest(
        '/api/leaderboard/seed-prompts?limit=6&featuredOnly=true'
      );
      setSeedPrompts(data.seedRuns || []);
    } catch (err) {
      logError('Failed to load seed prompts', err);
      setSeedPrompts([]);
    } finally {
      setLoadingSeed(false);
    }
  }

  async function loadRandomPrompt(categoryId: string) {
    setGenerating(categoryId);
    setError(null);
    try {
      const data = await apiRequest('/api/generate', {
        method: 'POST',
        body: JSON.stringify({
          category: categoryId,
          user_email: user?.email || undefined,
        }),
      });
      const generatedPrompt =
        typeof data?.prompt === 'string' ? data.prompt : null;
      if (!generatedPrompt) {
        throw new Error('No prompt returned from /api/generate');
      }
      setPrompt(generatedPrompt);
      // Note: data.prompt_id contains the auto-generated prompt ID if saved
    } catch (err: any) {
      logError('Prompt generation error', err);
      const message =
        err instanceof ApiError
          ? `Failed to generate prompt: ${err.message}`
          : `Failed to generate prompt: ${err?.message || 'Unknown error'}`;
      setError(message);
      const fallbackPrompt = getSamplePrompt(categoryId);
      if (fallbackPrompt) {
        setPrompt(fallbackPrompt);
        setTimeout(() => setError(null), 3000);
      }
    } finally {
      setGenerating(null);
    }
  }

  // Extracted arena creation logic - DRY
  async function createArenaRun(promptText: string) {
    setCreating(true);
    setError(null);
    try {
      const j = await apiRequest('/api/arena/start', {
        method: 'POST',
        body: JSON.stringify({
          prompt_text: promptText,
          user_email: user?.email || undefined,
        }),
      });
      if (!j.run_id) throw new Error('Invalid response');
      navigate(`/arena/${j.run_id}`);
    } catch (err: any) {
      // Provide user-friendly error messages
      let message = 'Failed to start battle. Please try again.';
      if (err.message?.includes('fetch')) {
        message = 'Unable to connect to server. Please check your connection.';
      } else if (err.status === 500) {
        message = 'Something went wrong on our end. Please try again.';
      } else if (err.message) {
        message = err.message;
      }
      setError(message);
      setCreating(false);
    }
  }

  async function startArena() {
    if (!prompt.trim()) return;

    // Check if user is authenticated
    if (!user) {
      // Store prompt in sessionStorage to survive OAuth redirect
      sessionStorage.setItem('pendingArenaPrompt', prompt.trim());
      setShowLoginModal(true);
      return;
    }

    await createArenaRun(prompt.trim());
  }

  // Check for pending arena creation after OAuth login
  useEffect(() => {
    const pendingPrompt = sessionStorage.getItem('pendingArenaPrompt');

    if (user && pendingPrompt) {
      logDebug('User logged in, creating arena with pending prompt');

      // Clear the pending prompt
      sessionStorage.removeItem('pendingArenaPrompt');

      // Create arena run
      createArenaRun(pendingPrompt);
    }
  }, [user, navigate]);

  const validRatings = leaderboard.filter(e => e.rating > 0).map(e => e.rating);
  const maxRating = validRatings.length > 0 ? Math.max(...validRatings) : 2000;
  const minRating = validRatings.length > 0 ? Math.min(...validRatings) : 1000;

  return (
    <div className="min-h-screen" style={{ background: '#F5F0EB' }}>
      {/* Styles */}
      <style>{`
        @keyframes cellPop {
          0% { 
            opacity: 0; 
            transform: scale(0) translateY(6px);
          }
          70% {
            opacity: 1;
            transform: scale(1.15) translateY(-1px);
          }
          100% { 
            opacity: 1; 
            transform: scale(1) translateY(0);
          }
        }
        @keyframes numberReveal {
          from { 
            opacity: 0;
            transform: translateY(8px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes gridReveal {
          from { opacity: 0; }
          to { opacity: 0.05; }
        }
        @keyframes sweepReveal {
          0% { 
            opacity: 0;
            transform: translateX(-100%);
          }
          50% {
            opacity: 0.12;
          }
          100% { 
            opacity: 0;
            transform: translateX(200%);
          }
        }
        .animate-fade-up {
          animation: fade-up 0.6s ease-out forwards;
          opacity: 0;
        }
        @keyframes gentlePulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes progressLine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        .gentle-pulse {
          animation: gentlePulse 2s ease-in-out infinite;
        }
        .progress-line {
          width: 25%;
          animation: progressLine 1.5s ease-in-out infinite;
        }
        .cell-pop {
          animation: cellPop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .number-reveal {
          animation: numberReveal 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          opacity: 0;
        }
        .grid-reveal {
          animation: gridReveal 1.5s ease-out forwards;
        }
        @keyframes gentleFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        @keyframes gentleFloat2 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(12px); }
        }
        @keyframes gentleFloat3 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .wave-primary {
          animation: gentleFloat 25s ease-in-out infinite;
        }
        .wave-secondary {
          animation: gentleFloat2 30s ease-in-out infinite;
        }
        .wave-tertiary {
          animation: gentleFloat3 22s ease-in-out infinite;
        }
        @keyframes battleCardIn {
          from { 
            opacity: 0; 
            transform: translateY(16px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        .battle-card {
          animation: battleCardIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          opacity: 0;
        }
      `}</style>

      {/* ========== HERO SECTION (RED) ========== */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            'linear-gradient(165deg, #9A2B3C 0%, #8B2332 20%, #6B1A27 60%, #4A1219 100%)',
        }}
      >
        {/* Grid pattern */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ opacity: 0.05 }}
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <pattern
              id="arenaGrid"
              width="80"
              height="80"
              patternUnits="userSpaceOnUse"
            >
              <rect
                width="80"
                height="80"
                fill="none"
                stroke="white"
                strokeWidth="0.5"
                opacity="0.8"
              />
              <line
                x1="26.66"
                y1="0"
                x2="26.66"
                y2="80"
                stroke="white"
                strokeWidth="0.5"
                opacity="0.4"
              />
              <line
                x1="53.33"
                y1="0"
                x2="53.33"
                y2="80"
                stroke="white"
                strokeWidth="0.5"
                opacity="0.4"
              />
              <line
                x1="0"
                y1="20"
                x2="80"
                y2="20"
                stroke="white"
                strokeWidth="0.5"
                opacity="0.25"
              />
              <line
                x1="0"
                y1="40"
                x2="80"
                y2="40"
                stroke="white"
                strokeWidth="0.5"
                opacity="0.25"
              />
              <line
                x1="0"
                y1="60"
                x2="80"
                y2="60"
                stroke="white"
                strokeWidth="0.5"
                opacity="0.25"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#arenaGrid)" />
        </svg>

        {/* Flowing mathematical curves with animated gradients */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 1400 800"
          preserveAspectRatio="xMidYMid slice"
          fill="none"
        >
          <defs>
            {/* Animated gradient for curve flow effect */}
            <linearGradient
              id="flowGradient1"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="white" stopOpacity="0">
                <animate
                  attributeName="offset"
                  values="-0.5;1"
                  dur="6s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="15%" stopColor="white" stopOpacity="0.25">
                <animate
                  attributeName="offset"
                  values="-0.35;1.15"
                  dur="6s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="30%" stopColor="white" stopOpacity="0">
                <animate
                  attributeName="offset"
                  values="-0.2;1.3"
                  dur="6s"
                  repeatCount="indefinite"
                />
              </stop>
            </linearGradient>

            <linearGradient
              id="flowGradient2"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="white" stopOpacity="0">
                <animate
                  attributeName="offset"
                  values="-0.5;1"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="20%" stopColor="white" stopOpacity="0.2">
                <animate
                  attributeName="offset"
                  values="-0.3;1.2"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="40%" stopColor="white" stopOpacity="0">
                <animate
                  attributeName="offset"
                  values="-0.1;1.4"
                  dur="8s"
                  repeatCount="indefinite"
                />
              </stop>
            </linearGradient>

            <linearGradient
              id="flowGradient3"
              x1="100%"
              y1="0%"
              x2="0%"
              y2="0%"
            >
              <stop offset="0%" stopColor="white" stopOpacity="0">
                <animate
                  attributeName="offset"
                  values="-0.5;1"
                  dur="7s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="15%" stopColor="white" stopOpacity="0.18">
                <animate
                  attributeName="offset"
                  values="-0.35;1.15"
                  dur="7s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="30%" stopColor="white" stopOpacity="0">
                <animate
                  attributeName="offset"
                  values="-0.2;1.3"
                  dur="7s"
                  repeatCount="indefinite"
                />
              </stop>
            </linearGradient>
          </defs>

          {/* Base curves - static, very subtle */}
          <path
            className="wave-primary"
            d="M-200 120 Q 200 80, 500 140 Q 800 200, 1100 120 Q 1400 40, 1600 100"
            stroke="white"
            strokeWidth="1"
            strokeOpacity="0.04"
            fill="none"
          />
          <path
            className="wave-secondary"
            d="M-200 220 Q 150 280, 450 220 Q 750 160, 1050 240 Q 1350 320, 1600 260"
            stroke="white"
            strokeWidth="1"
            strokeOpacity="0.03"
            fill="none"
          />
          <path
            className="wave-tertiary"
            d="M-200 380 Q 200 320, 500 400 Q 800 480, 1100 380 Q 1400 280, 1600 360"
            stroke="white"
            strokeWidth="1.5"
            strokeOpacity="0.05"
            fill="none"
          />
          <path
            className="wave-primary"
            d="M-200 520 Q 250 600, 550 520 Q 850 440, 1150 540 Q 1450 640, 1600 580"
            stroke="white"
            strokeWidth="1"
            strokeOpacity="0.04"
            fill="none"
          />
          <path
            className="wave-secondary"
            d="M-200 680 Q 300 620, 600 700 Q 900 780, 1200 680 Q 1500 580, 1600 640"
            stroke="white"
            strokeWidth="0.75"
            strokeOpacity="0.03"
            fill="none"
          />

          {/* Animated flow overlays - light pulses traveling along curves */}
          <path
            className="wave-primary"
            d="M-200 120 Q 200 80, 500 140 Q 800 200, 1100 120 Q 1400 40, 1600 100"
            stroke="url(#flowGradient1)"
            strokeWidth="2"
            fill="none"
          />
          <path
            className="wave-tertiary"
            d="M-200 380 Q 200 320, 500 400 Q 800 480, 1100 380 Q 1400 280, 1600 360"
            stroke="url(#flowGradient2)"
            strokeWidth="2.5"
            fill="none"
          />
          <path
            className="wave-primary"
            d="M-200 520 Q 250 600, 550 520 Q 850 440, 1150 540 Q 1450 640, 1600 580"
            stroke="url(#flowGradient3)"
            strokeWidth="2"
            fill="none"
          />
        </svg>

        {/* Content - pt-20 accounts for nav height (h-14) plus spacing */}
        <div className="relative z-10 max-w-3xl mx-auto px-6 pt-20 pb-10">
          {/* Logo + Title */}
          <header className="text-center mb-6 animate-fade-up">
            <div className="flex justify-center mb-4">
              <Logo size={48} variant="light" />
            </div>
            <h1
              className="font-serif font-normal tracking-tight mb-2"
              style={{
                color: '#FFFFFF',
                fontSize: 'clamp(2rem, 6vw, 3.5rem)',
                lineHeight: 1.1,
              }}
            >
              Spreadsheet Arena
            </h1>
            <p
              className="text-base max-w-md mx-auto"
              style={{ color: 'rgba(255, 255, 255, 0.65)' }}
            >
              Which AI builds the best spreadsheets? Vote and shape the
              benchmark.
            </p>
          </header>

          {/* Category pills */}
          {AUTOGENERATED_PROMPT_BUTTONS && (
            <div
              className="flex flex-wrap justify-center gap-2 mb-5 animate-fade-up"
              style={{ animationDelay: '100ms' }}
            >
              {CATEGORIES.map(cat => {
                const isActive = generating === cat.id;
                const isDisabled = generating !== null && !isActive;

                return (
                  <button
                    key={cat.id}
                    onClick={() => loadRandomPrompt(cat.id)}
                    disabled={generating !== null}
                    className={`
                      px-4 py-2 rounded-full text-sm font-medium transition-all duration-200
                      ${!isDisabled && !isActive ? 'hover:bg-white hover:text-[#8B2332] hover:border-white hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-md' : ''}
                    `}
                    style={{
                      background: isActive
                        ? 'rgba(255,255,255,0.95)'
                        : 'rgba(255,255,255,0.08)',
                      color: isActive
                        ? '#8B2332'
                        : isDisabled
                          ? 'rgba(255,255,255,0.3)'
                          : 'rgba(255,255,255,0.9)',
                      border: isActive
                        ? '1px solid white'
                        : '1px solid rgba(255,255,255,0.15)',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {isActive ? (
                      <span className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#8B2332] animate-pulse" />
                        {cat.label}
                      </span>
                    ) : (
                      cat.label
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Input card */}
          <div className="animate-fade-up" style={{ animationDelay: '150ms' }}>
            <div
              className="relative rounded-xl overflow-hidden"
              style={{
                background: '#FFFFFF',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              }}
            >
              {generating ? (
                <div
                  className="relative min-h-[100px] flex flex-col items-center justify-center"
                  style={{ background: '#FFFFFF' }}
                >
                  {/* Elegant content */}
                  <div className="flex items-center gap-3 mb-4">
                    <LogoAnimated size={24} variant="default" />
                    <span
                      className="text-sm font-medium gentle-pulse"
                      style={{ color: '#8B2332' }}
                    >
                      Generating prompt...
                    </span>
                  </div>

                  {/* Thin progress line */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-0.5 overflow-hidden"
                    style={{ background: '#F0EDE8' }}
                  >
                    <div
                      className="progress-line h-full"
                      style={{ background: '#8B2332' }}
                    />
                  </div>
                </div>
              ) : (
                <textarea
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  onKeyDown={e => {
                    // Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux)
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                      e.preventDefault();
                      startArena();
                    }
                  }}
                  placeholder="Describe the spreadsheet you want AI models to build..."
                  rows={3}
                  className="w-full px-5 py-4 text-base resize-none border-none outline-none"
                  style={{
                    background: 'transparent',
                    color: '#1A1A1A',
                    lineHeight: 1.6,
                  }}
                />
              )}

              <div
                className="px-4 py-3 flex items-center justify-between"
                style={{
                  background: '#FAFAF8',
                  borderTop: '1px solid #F0EDE8',
                }}
              >
                {error ? (
                  <PromptError message={error} />
                ) : (
                  <PromptLengthIndicator
                    length={prompt.length}
                    maxLength={MAX_PROMPT_LENGTH}
                    isApproachingLimit={isApproachingLimit}
                    isOverLimit={isPromptTooLong}
                  />
                )}
                <button
                  onClick={startArena}
                  disabled={
                    !prompt.trim() ||
                    creating ||
                    generating !== null ||
                    isPromptTooLong
                  }
                  className="px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200"
                  style={{
                    background:
                      !prompt.trim() ||
                      creating ||
                      generating ||
                      isPromptTooLong
                        ? '#E8E4DF'
                        : '#8B2332',
                    color:
                      !prompt.trim() ||
                      creating ||
                      generating ||
                      isPromptTooLong
                        ? '#A09A94'
                        : '#FFFFFF',
                    cursor:
                      !prompt.trim() ||
                      creating ||
                      generating ||
                      isPromptTooLong
                        ? 'not-allowed'
                        : 'pointer',
                  }}
                >
                  {creating ? 'Starting...' : 'Start Battle'}
                </button>
              </div>
            </div>
          </div>

          {/* Stats with count-up animation */}
          <div className="flex justify-center gap-16 mt-8 pb-16">
            <StatDisplay
              value={stats.totalBattles}
              label="Battles"
              delay={300}
              animate
            />
            <StatDisplay
              value={stats.totalModels}
              label="Models"
              delay={450}
              animate
            />
            <StatDisplay
              value={formatModelName(
                leaderboard[0]?.display_name || '?',
                'short'
              )}
              label="Leader"
              delay={600}
            />
          </div>
        </div>

        {/* Curved section divider - red to cream (inside hero to match gradient) */}
        <svg
          className="block w-full -mb-px"
          viewBox="0 0 1440 80"
          preserveAspectRatio="none"
        >
          <path
            d="M0,30 Q360,70 720,30 Q1080,-10 1440,30 L1440,80 L0,80 Z"
            fill="#F5F0EB"
          />
        </svg>
      </section>

      {/* ========== OVERALL RANKINGS (CREAM) ========== */}
      <section className="pt-12 pb-20 px-6" style={{ background: '#F5F0EB' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2
                className="font-serif text-3xl font-normal mb-2"
                style={{ color: '#1A1A1A' }}
              >
                Overall Rankings
              </h2>
              <p className="text-base mb-16" style={{ color: '#6B6560' }}>
                Ratings derived from {stats.totalBattles.toLocaleString()}{' '}
                battles over {stats.totalPrompts.toLocaleString()} tasks.
                <a
                  href="#methodology"
                  className="ml-0.5 hover:opacity-70 transition-opacity"
                  style={{ color: '#8B2332' }}
                >
                  <sup>1</sup>
                </a>
              </p>
            </div>
            <Link
              to="/leaderboard"
              className="text-sm font-medium flex items-center gap-1 transition-opacity hover:opacity-70"
              style={{ color: '#8B2332' }}
            >
              Full leaderboard
              <ChevronRight size={16} />
            </Link>
          </div>

          {loadingLeaderboard ? (
            <div className="py-24 flex justify-center">
              <LogoAnimated size={36} variant="default" />
            </div>
          ) : leaderboard.length > 0 ? (
            <div
              className="flex items-end justify-between gap-3"
              style={{ height: '320px' }}
            >
              {leaderboard.slice(0, 10).map((entry, idx) => {
                const rating = entry.rating || 0;
                const range = maxRating - minRating || 1;
                const normalized =
                  rating > 0 ? (rating - minRating) / range : 0;
                const heightPx = Math.max(60, normalized * 220 + 60);
                const isHovered = hoveredBar === idx;

                return (
                  <div
                    key={entry.model_key || idx}
                    className="flex-1 flex flex-col items-center cursor-pointer"
                    style={{ maxWidth: '85px' }}
                    onMouseEnter={() => setHoveredBar(idx)}
                    onMouseLeave={() => setHoveredBar(null)}
                  >
                    <div
                      className="font-serif text-sm font-medium mb-2 tabular-nums transition-all duration-200"
                      style={{
                        color: isHovered ? '#8B2332' : '#1A1A1A',
                        transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                        height: '20px',
                      }}
                    >
                      {rating || '—'}
                    </div>

                    <div
                      className="w-full rounded-t-lg transition-all duration-200"
                      style={{
                        height: `${heightPx}px`,
                        backgroundColor:
                          BAR_COLORS[idx] || BAR_COLORS[BAR_COLORS.length - 1],
                        transform: isHovered ? 'scaleY(1.02)' : 'scaleY(1)',
                        transformOrigin: 'bottom',
                      }}
                    />

                    {/* Logo + Model Name - fixed height to prevent layout shift */}
                    <div
                      className="mt-3 flex flex-col items-center gap-1.5"
                      style={{ minHeight: '56px' }}
                    >
                      <div
                        className="transition-transform duration-200"
                        style={{
                          transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                        }}
                      >
                        <ProviderLogo
                          modelKey={entry.model_key || ''}
                          size={24}
                          showBackground={true}
                        />
                      </div>
                      <div
                        className="text-xs text-center leading-tight transition-colors duration-200"
                        style={{
                          color: isHovered ? '#1A1A1A' : '#6B6560',
                          maxWidth: '80px',
                        }}
                      >
                        {formatModelName(
                          entry.display_name || entry.model_key || 'Unknown'
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-24 text-center">
              <p className="text-sm" style={{ color: '#6B6560' }}>
                No ranking data yet
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ========== SEED PROMPTS (CREAM) - Meridian users only ========== */}
      {isMeridianUser && !loadingSeed && seedPrompts.length > 0 && (
        <SeedPromptsSection prompts={seedPrompts} />
      )}

      {/* Curved divider - cream to dark */}
      <CurvedDivider fromColor="#F5F0EB" toColor="#1A1A1A" />

      {/* ========== RECENT BATTLES (DARK) ========== */}
      <section className="pt-12 pb-20 px-6" style={{ background: '#1A1A1A' }}>
        <div className="max-w-5xl mx-auto">
          <SectionHeader
            title="Recent Battles"
            subtitle="Live from the community"
            linkTo={isMeridianUser ? '/community-votes' : undefined} // component doesn't render null so this works
            variant="dark"
          />

          {loadingRecent ? (
            <div className="py-16 flex justify-center">
              <LogoAnimated size={32} variant="light" />
            </div>
          ) : recentVotes.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recentVotes.slice(0, 6).map((vote, idx) => (
                <RecentBattleCard key={vote.runId} vote={vote} index={idx} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="mb-6" style={{ color: 'rgba(255,255,255,0.4)' }}>
                No recent battles yet
              </p>
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-6 py-3 rounded-xl text-sm font-semibold"
                style={{ background: '#8B2332', color: '#FFF' }}
              >
                Be the first
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Curved divider - dark to cream */}
      <CurvedDivider fromColor="#1A1A1A" toColor="#F5F0EB" flip />

      {/* ========== HOW IT WORKS (CREAM) ========== */}
      <HowItWorksSection />

      {/* ========== METHODOLOGY FOOTNOTE ========== */}
      <section
        id="methodology"
        className="py-6 px-6"
        style={{ background: '#F5F0EB', borderTop: '1px solid #E8E3DD' }}
      >
        <div className="max-w-5xl mx-auto">
          <p className="text-xs" style={{ color: '#A9A49E' }}>
            <sup>1</sup> Ratings are{' '}
            <a
              href="https://inria.hal.science/inria-00149859/document#page=3"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline"
              style={{ color: '#8B2332' }}
            >
              Bradley-Terry-Davidson
            </a>{' '}
            model strengths transformed onto an Elo-like scale with the
            lowest-scoring model anchored to 1000.
          </p>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <Footer />

      {/* Login Modal for Start Battle */}
      {showLoginModal && (
        <LoginModal
          onClose={() => {
            setShowLoginModal(false);
          }}
        />
      )}
    </div>
  );
}
